import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { ActivityDataTemplate, RootState } from '../../../../types/common.types';
import {
  ActivityEngagement,
  Comment,
  HashtagEngagement,
  Post,
  Reaction,
  TribeTemplate
} from '../../../../types/entity.types';

import { useAdministrationApi } from '../../../../api/administration/useAdministrationApi';
import { useTribeTemplateApi } from '../../../../api/tribe-template/useTribeTemplateApi';

import { SHORT_MONTHS } from '../../../../utils/constants';
import { formatUsername } from '../../../../utils/util';

import TribeMemberEngagement from '../../../../components/screens/programme/tribe/tribe-member-engagement/TribeMemberEngagement';

const TribeMemberEngagementContainer = () => {
  const [activityData, setActivityData] = useState<ActivityDataTemplate[]>([]);
  const [hashtagData, setHashtagData] = useState<unknown[]>([]);

  const { activityEngagement, hashtagEngagement, tribeTemplate } = useSelector(({ tribe }: RootState) => tribe);

  const { status, programmeExternalReference } = useParams();

  const navigate = useNavigate();

  const { viewActivityEngagement, viewHashtagEngagement } = useAdministrationApi();

  const { viewTribeTemplate } = useTribeTemplateApi();

  const [searchParams] = useSearchParams();

  const hasSubProgrammes = searchParams.get('hasSubProgrammes') === 'true';

  const mapActivityEngagement = useCallback((data: ActivityEngagement[]) => {
    const mappedData = data.map((item, index) => {
      const { userName, lastOnline, posts, reactions, comments } = item;

      const totalPosts = posts.length;
      const totalLikes = reactions.length;
      const totalComments = comments.length;

      return {
        '#': index + 1,
        username: formatUsername(userName),
        lastSeen: lastOnline ? formatDate(lastOnline) : '',
        postsCreated: totalPosts,
        likes: totalLikes,
        comments: totalComments,
        all: totalPosts + totalLikes + totalComments
      };
    });

    setActivityData(mappedData);
  }, []);

  const mapHashtagEngagement = useCallback(
    (data: HashtagEngagement[]) => {
      const { hashTags } = tribeTemplate as TribeTemplate;

      const mappedData = data.map((item, index) => {
        const mappedItem: Record<string, string | number> = {
          '#': index + 1,
          username: formatUsername(item.userName)
        };

        hashTags?.forEach((hashtag) => {
          const text = hashtag.text!;
          mappedItem[text] = item.posts.filter((post) => !!post?.hashTag?.includes(text)).length;
        });

        return mappedItem;
      });

      setHashtagData(mappedData);
    },
    [tribeTemplate]
  );

  useEffect(() => {
    viewActivityEngagement({ programmeExternalReference: programmeExternalReference! });
    viewTribeTemplate({ programmeExternalReference: programmeExternalReference! });
    viewHashtagEngagement({ programmeExternalReference: programmeExternalReference! });
  }, [programmeExternalReference, viewActivityEngagement, viewHashtagEngagement, viewTribeTemplate]);

  useEffect(() => {
    if (activityEngagement.length > 0) {
      mapActivityEngagement(activityEngagement);
    }

    if (tribeTemplate && hashtagEngagement.length > 0) {
      mapHashtagEngagement(hashtagEngagement);
    }
  }, [activityEngagement, hashtagEngagement, tribeTemplate, mapActivityEngagement, mapHashtagEngagement]);

  const formatDate = (date: string) => {
    if (!date) {
      return '';
    }

    const dateObj = new Date(date);
    dateObj.setHours(dateObj.getHours() - 2);
    let day: number | string = dateObj.getDate();
    const month = SHORT_MONTHS[dateObj.getMonth()];
    let hour: number | string = dateObj.getHours();
    let minute: number | string = dateObj.getMinutes();

    if (day < 10) {
      day = `0${day}`;
    }

    if (hour < 10) {
      hour = `0${hour}`;
    }

    if (minute < 10) {
      minute = `0${minute}`;
    }

    return `${day} ${month} ${hour}:${minute}`;
  };

  const applyDateFilter = (fromDate?: Date, toDate?: Date) => {
    const copyOfActivityEngagement = structuredClone(activityEngagement);
    copyOfActivityEngagement.forEach((item) => {
      item.posts = item.posts.filter((post) => filter(post, fromDate, toDate));
      item.comments = item.comments.filter((comment) => filter(comment, fromDate, toDate));
      item.reactions = item.reactions.filter((reaction) => filter(reaction, fromDate, toDate));
    });
    mapActivityEngagement(copyOfActivityEngagement);

    const copyOfHashtagEngagement = structuredClone(hashtagEngagement);
    copyOfHashtagEngagement.forEach((item) => {
      item.posts = item.posts.filter((post) => filter(post, fromDate, toDate));
    });
    mapHashtagEngagement(copyOfHashtagEngagement);
  };

  const filter = (item: Post | Comment | Reaction, fromDate?: Date, toDate?: Date) => {
    const date = new Date(item.startDate as string);
    if (fromDate && toDate) {
      return date >= fromDate && date <= toDate;
    }

    if (fromDate) {
      return date >= fromDate;
    }

    return !!toDate && date <= toDate;
  };

  const navigateBack = () => {
    navigate(`/programmes/${status}/${programmeExternalReference}/tribe?hasSubProgrammes=${hasSubProgrammes}`);
  };

  return (
    <TribeMemberEngagement
      activityData={activityData}
      hashtagData={hashtagData}
      hashtags={tribeTemplate?.hashTags}
      applyDateFilter={applyDateFilter}
      navigateBack={navigateBack}
    />
  );
};

export default TribeMemberEngagementContainer;
